/*
 * Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import { Fragment, jsx, setClassToken } from '@amazon/vinyl-tsx'
import { createDisposer, debounce, throttle } from '@amazon/vinyl-util'
import { data, type MutableValue, type ObservableValue } from '@amazon/vinyl-observable'
import { Icon } from '@/components/Icon'
import { Link } from '@/components/Link'
import {
    clearSearch,
    getSearchMode,
    getSearchSubmitted,
    preloadSearchRoute,
    registerSearchInput,
    submitSearchOnPage,
    showSearch as showSearchRoute,
    updateSearchQuery,
} from '@/search/searchState'
import { trapDialogFocus } from '@/components/dialogFocus'
import { windowEvents } from '@/util/interaction'
import { getAppScroller } from '@/util/domUtil'
import { prefersReducedMotion } from '@/util/reducedMotion'
import { getString } from '@/header/i18n/bundle.generated'
import { askAiNavVisible$ } from '@/header/Header'
import { ProfileAvatarButton } from '@/header/ProfileAvatarButton'
import { canAccessAudiobooks$, canAccessPodcastsAndAudiobooks$ } from '@/header/childProfileNavGate'
import { FeatureGateDisabled } from '@/components/FeatureGate'
import type { IconName } from '@/components/Icons'
import { isShownPath } from '@/router/router'
import { pressInteraction, reportShellClick, reportShellNavigation } from '@/metrics/shellUiMetrics'
import type { UiClick } from '@/metrics/uiMetrics'

export type MobileTopBarProps = {
    // Shared with the desktop avatar so the SAME HeaderMenu popover backs
    // both taps — a second menu instance would fight for the singleton.
    readonly headerMenuVisible: MutableValue<boolean>
}

// Mirrors the desktop header nav set so the logo overlay offers the same
// destinations. Labels stay observable — reading `.value` at render time
// would freeze the string before the i18n bundle resolves.
type MobileNavEntry = {
    readonly href: string
    /** GH's click for the same destination; Ask AI has none. */
    readonly actionType?: string
    readonly icon: IconName
    readonly label: ObservableValue<string>
    // Gates the entry's visibility; omitted entries are always shown. Podcasts
    // and Audiobooks are withheld from child profiles (GH Kids Mode).
    readonly visible?: ObservableValue<boolean>
}

function mobileNavEntries(): readonly MobileNavEntry[] {
    return [
        {
            href: '/',
            actionType: 'goBrowse',
            icon: 'homeFilled',
            label: getString('dmusic_layout_home'),
        },
        {
            href: '/feed',
            icon: 'chatBubble',
            label: getString('dmusic_layout_chat'),
            visible: askAiNavVisible$(),
        },
        {
            href: '/music',
            actionType: 'goMusicHome',
            icon: 'myMusic',
            label: getString('dmusic_browse_home_explore_music_button_label'),
        },
        {
            href: '/podcasts',
            actionType: 'goPodcastHomePage',
            icon: 'podcasts',
            label: getString('dmusic_library_podcasts_pill_title'),
            visible: canAccessPodcastsAndAudiobooks$,
        },
        {
            href: '/audiobooks',
            actionType: 'goAudiobooksHome',
            icon: 'audibleLight',
            label: getString('dmusic_browse_home_explore_audiobooks_button_label'),
            visible: canAccessAudiobooks$,
        },
        {
            href: '/my/library',
            actionType: 'goMyMusic',
            icon: 'library',
            label: getString('dmusic_layout_library'),
        },
    ]
}

export function MobileTopBar(props: MobileTopBarProps) {
    const menuOpen$: MutableValue<boolean> = data(false)
    const closeMenu = () => (menuOpen$.value = false)

    const bar = (
        <Fragment>
            <header
                className="mobileTopBar"
                onConnect={(el) => {
                    // Hide the bar on scroll-down, reveal on scroll-up (and
                    // always at the top). `sticky` alone keeps the bar pinned
                    // once it scrolls back into range; the direction toggle
                    // lets it slide out of the way while reading down-page.
                    // Skip the slide transition when the user asked for less
                    // motion — the bar then snaps between shown/hidden.
                    setClassToken(el, 'mobileTopBarNoMotion', prefersReducedMotion())
                    // Reflow once at connect — the height is stable, so
                    // reading it per scroll event would thrash layout.
                    const barHeight = el.offsetHeight
                    // Only hide once the reader has committed to a downward
                    // scroll; a small budget absorbs the jitter/rubber-band
                    // that would otherwise flicker the bar. ANY upward move
                    // reveals immediately (the reported bug: a symmetric
                    // threshold swallowed slow finger-drag scroll-ups, so the
                    // bar stayed hidden mid-page), and resets the budget.
                    const HIDE_THRESHOLD = 8
                    // The page scrolls on `#main-content`, not the document, so
                    // read its scrollTop and listen on IT — a window 'scroll'
                    // event never fires for a container scroll.
                    const scroller = getAppScroller()
                    let lastY = scroller?.scrollTop ?? 0
                    let downTravel = 0
                    const update = () => {
                        const y = scroller?.scrollTop ?? 0
                        const delta = y - lastY
                        lastY = y
                        if (y <= barHeight) {
                            // Near the top the bar is always shown.
                            downTravel = 0
                            setClassToken(el, 'mobileTopBarHidden', false)
                        } else if (delta < 0) {
                            downTravel = 0
                            setClassToken(el, 'mobileTopBarHidden', false)
                        } else if (delta > 0) {
                            downTravel += delta
                            if (downTravel > HIDE_THRESHOLD) {
                                setClassToken(el, 'mobileTopBarHidden', true)
                            }
                        }
                    }
                    // Throttle to coalesce the burst of scroll events into
                    // ~frame-rate work; trailing keeps the final position
                    // authoritative so a scroll that ends mid-flick still
                    // settles to the right shown/hidden state.
                    const onScroll = throttle(update, 0.05, {
                        leading: true,
                        trailing: true,
                    })
                    const { add, dispose } = createDisposer()
                    add(onScroll)
                    scroller?.addEventListener('scroll', onScroll, { passive: true })
                    add(() => scroller?.removeEventListener('scroll', onScroll))
                    add(
                        getSearchMode().onData((searchMode) => {
                            setClassToken(el, 'mobileTopBarSearchMode', searchMode)
                        })
                    )
                    return dispose
                }}
            >
                <button
                    className="mobileTopBarLogoBtn button"
                    aria-label={getString('dmusic_amazon_music_open_menu_label')}
                    title={getString('dmusic_amazon_music_open_menu_label')}
                    aria-haspopup="dialog"
                    onclick={(event) => {
                        event.stopImmediatePropagation()
                        menuOpen$.value = !menuOpen$.value
                    }}
                >
                    {/* Hamburger + logo are one button: the icon signals the tap
                        opens the menu, and the state stays identical while open so
                        the trigger doesn't morph out from under the finger. */}
                    <Icon icon="menu" className="mobileTopBarMenuIcon" />
                    <span className="logoLink mobileTopBarLogo" aria-hidden="true"></span>
                </button>

                <div className="mobileTopBarTrailing">
                    {/* Amp off: BottomToolbar (which owns search on mobile) doesn't
                        mount, so the top bar carries its own search entry — matching
                        the desktop header's ampOff-restores-search behavior. Grouped
                        next to the avatar so it sits at the trailing edge. */}
                    <FeatureGateDisabled flag="amp" create={() => <MobileSearchBar />} />

                    {/* Reuse the desktop avatar so the mobile bar renders the
                        real profile image with the same fallback; the extra class
                        only sizes/positions it for the compact bar. */}
                    <ProfileAvatarButton
                        headerMenuVisible={props.headerMenuVisible}
                        className="mobileTopBarAvatar"
                    />
                </div>
            </header>

            <MobileNavOverlay menuOpen$={menuOpen$} closeMenu={closeMenu} />
        </Fragment>
    )

    return bar
}

/**
 * Compact search entry for the mobile top bar — a toggle icon idle, expanding
 * to the full bar width (in place of the logo + avatar) once `/search` is
 * entered. Only mounted when amp is off; amp-on relies on BottomToolbar.
 */
function MobileSearchBar() {
    const view = (
        <div className="mobileTopBarSearch">
            <button
                className="mobileTopBarSearchBtn button"
                aria-label={getString('dmusic_amazon_music_search_label')}
                title={getString('dmusic_amazon_music_search_label')}
                onmouseover={preloadSearchRoute}
                onfocus={preloadSearchRoute}
                onclick={(event: MouseEvent) => {
                    reportShellClick({
                        actionType: 'goSearch',
                        interactionType: pressInteraction(event),
                    })
                    // Route entry is an async chunk load (`/search`'s lazy
                    // factory), so focusing off the later searchMode
                    // transition alone lands outside the tap's user-gesture
                    // window — iOS then won't raise the on-screen keyboard.
                    // Focus synchronously here too, mirroring the desktop
                    // header's showSearch().
                    showSearchRoute()
                    searchInput.focus()
                }}
            >
                <Icon icon="search" />
            </button>
            <form
                action="#"
                autocomplete="off"
                className="mobileTopBarSearchForm"
                onsubmit={(event: Event) => {
                    event.preventDefault()
                    const query = searchInput.value.trim()
                    const click: UiClick = {
                        actionType: 'executeSearch',
                        searchQuery: query,
                        interactionType: 'KEYBOARD',
                    }
                    if (submitSearchOnPage(query, click)) return
                    if (query) reportShellClick(click)
                    getSearchSubmitted().value = true
                    submit()
                }}
            >
                <button
                    type="button"
                    className="mobileTopBarSearchCancel button"
                    aria-label={getString('dmusic_clear_search')}
                    title={getString('dmusic_clear_search')}
                    onclick={() => clearSearch()}
                >
                    <Icon icon="close" />
                </button>
                <input
                    className="mobileTopBarSearchInput"
                    type="search"
                    placeholder={getString('dmusic_amazon_music_search_placeholder_text')}
                    value=""
                    oninput={debounce(() => {
                        getSearchSubmitted().value = false
                        submit()
                    }, 0.6)}
                    onConnect={(inputView) => {
                        const input = inputView as HTMLInputElement
                        const { add, dispose } = createDisposer()
                        add(registerSearchInput(input))
                        add(
                            getSearchMode().onData((searchMode) => {
                                if (searchMode) {
                                    const params = new URLSearchParams(window.location.search)
                                    input.value = params.get('q') ?? ''
                                    // Focus synchronously to stay inside the tap's
                                    // user-gesture window (iOS keyboard requires it).
                                    input.focus()
                                } else {
                                    input.blur()
                                    input.value = ''
                                }
                            })
                        )
                        return dispose
                    }}
                />
            </form>
        </div>
    )

    const searchInput = view.querySelector<HTMLInputElement>('.mobileTopBarSearchInput')!

    function submit() {
        const query = searchInput.value.trim()
        if (!window.location.pathname.startsWith('/search')) {
            showSearchRoute(query)
        } else {
            updateSearchQuery(query)
        }
    }

    return view
}

function MobileNavOverlay(props: { menuOpen$: MutableValue<boolean>; closeMenu: () => void }) {
    const { menuOpen$, closeMenu } = props

    const overlay = (
        <div
            className="mobileNavOverlay"
            role="dialog"
            aria-modal="true"
            aria-label={getString('dmusic_header_amazon_music_navigation_label')}
            visible={menuOpen$}
            classList={[menuOpen$.map((open) => (open ? 'mobileNavOverlayOpen' : ''))]}
            onConnect={(view) => {
                const { add, dispose } = createDisposer()
                let releaseFocus: (() => void) | null = null
                const release = () => {
                    releaseFocus?.()
                    releaseFocus = null
                }
                add(
                    menuOpen$.onData((open) => {
                        if (!open) release()
                        else releaseFocus ??= trapDialogFocus(view, { close: closeMenu })
                    })
                )
                add(release)
                // The header logo button's stopImmediatePropagation keeps the opening tap from this handler.
                add(
                    windowEvents.on('click', (event) => {
                        if (!menuOpen$.value) return
                        const target = event.target as Node
                        // Clicks on the panel itself are handled by the row /
                        // close button; only backdrop clicks fall through here.
                        if (panel.contains(target)) return
                        closeMenu()
                    })
                )
                return dispose
            }}
        >
            <div className="mobileNavPanel">
                <button
                    className="mobileNavClose button"
                    data-dialog-dismiss=""
                    aria-label={getString('dmusic_dialogue_close_accessibility_label')}
                    title={getString('dmusic_dialogue_close_accessibility_label')}
                    onclick={closeMenu}
                >
                    <Icon icon="close" />
                </button>

                <span className="logoLink mobileNavLogo" aria-hidden="true"></span>

                <nav
                    className="mobileNavLinks"
                    aria-label={getString('dmusic_header_amazon_music_navigation_label')}
                >
                    {...mobileNavEntries().map((entry) => (
                        <Link
                            className="mobileNavLink button"
                            href={entry.href}
                            // Explicit tabindex so Safari keeps these anchors in
                            // the Tab order — WebKit drops a plain <a href> from
                            // sequential focus unless "tab to links" is enabled.
                            tabIndex={0}
                            visible={entry.visible ?? true}
                            onclick={(event: MouseEvent) => {
                                if (!entry.actionType) return
                                reportShellNavigation(
                                    {
                                        actionType: entry.actionType,
                                        interactionType: pressInteraction(event),
                                    },
                                    isShownPath(entry.href)
                                )
                            }}
                            // Dismiss the overlay via `beforeNavigate`, the hook
                            // Link fires on in-app nav.
                            beforeNavigate={() => {
                                clearSearch()
                                closeMenu()
                            }}
                            navigateOptions={{ scroll: 'restore' }}
                        >
                            <Icon icon={entry.icon} className="mobileNavLinkIcon" />
                            <span className="mobileNavLinkLabel">{entry.label}</span>
                        </Link>
                    ))}
                </nav>
            </div>
        </div>
    )

    const panel = overlay.querySelector<HTMLElement>('.mobileNavPanel')!
    return overlay
}
